import { useApolloClient } from '@apollo/client';
import { faFolder, faFile, faFileExcel, faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '../../../../core/components/Checkbox.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { GET_SHAREPOINT_ITEMS } from '../../../../customer/queries.ts';
import { KNOWLEDGE_SUPPORTED_EXTENSIONS } from '../../constants.ts';
import { SharePointSelectedItem } from '../config/ConfigSteps.tsx';

const ChevronRightIcon = withIcon(faChevronRight);
const ChevronDownIcon = withIcon(faChevronDown);
const FolderIcon = withIcon(faFolder);
const FileIcon = withIcon(faFile);
const ExcelIcon = withIcon(faFileExcel);
const PdfIcon = withIcon(faFilePdf);

interface SharePointItem {
    id: string;
    name: string;
    webUrl: string;
    isFolder: boolean;
}

interface TreeNode extends SharePointItem {
    children?: TreeNode[];
    isLoaded: boolean;
    isExpanded: boolean;
}

interface SharePointTreeBrowserProps {
    integrationId: string;
    driveId: string;
    selectedItems: SharePointSelectedItem[];
    onSelectedItemsChange: (items: SharePointSelectedItem[]) => void;
}

const getFileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return PdfIcon;
    if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return ExcelIcon;
    return FileIcon;
};

const isFileSupported = (name: string): boolean => {
    const ext = '.' + (name.split('.').pop()?.toLowerCase() || '');
    return KNOWLEDGE_SUPPORTED_EXTENSIONS.has(ext);
};

/** Walk the in-memory tree and collect canonical selected items (folders subsume descendants). */
const buildSelectedItemsWithPaths = (
    nodes: TreeNode[],
    selectedSet: Set<string>,
    parentPath: string[] = [],
    ancestorSelected = false
): SharePointSelectedItem[] => {
    const result: SharePointSelectedItem[] = [];
    for (const node of nodes) {
        const isSelected = selectedSet.has(node.id);
        if (isSelected && !ancestorSelected) {
            result.push({ id: node.id, path: parentPath });
        }
        if (node.children) {
            result.push(
                ...buildSelectedItemsWithPaths(
                    node.children,
                    selectedSet,
                    [...parentPath, node.id],
                    ancestorSelected || isSelected
                )
            );
        }
    }
    return result;
};

const SharePointTreeBrowser = ({
    integrationId,
    driveId,
    selectedItems,
    onSelectedItemsChange,
}: SharePointTreeBrowserProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const client = useApolloClient();
    const [nodes, setNodes] = useState<TreeNode[]>([]);
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const selectedIds = useMemo(() => selectedItems.map((item) => item.id), [selectedItems]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Derive which folders need expanding from the stored paths
    const initialExpandedFolderIds = useMemo(() => {
        const folderIds = new Set<string>();
        for (const item of selectedItems) {
            for (const folderId of item.path) {
                folderIds.add(folderId);
            }
        }
        return folderIds;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

    const fetchChildren = useCallback(
        async (itemId?: string): Promise<TreeNode[]> => {
            const { data } = await client.query({
                query: GET_SHAREPOINT_ITEMS,
                variables: { integrationId, driveId, itemId: itemId || null },
                fetchPolicy: 'network-only',
            });

            return (data?.sharepointItems ?? []).map((item: SharePointItem) => ({
                ...item,
                children: undefined,
                isLoaded: false,
                isExpanded: false,
            }));
        },
        [client, integrationId, driveId]
    );

    const loadWithExpandedFolders = useCallback(
        async (expandedSet: Set<string>, itemId?: string): Promise<TreeNode[]> => {
            const items = await fetchChildren(itemId);
            const result: TreeNode[] = [];
            for (const item of items) {
                if (item.isFolder && expandedSet.has(item.id)) {
                    const children = await loadWithExpandedFolders(expandedSet, item.id);
                    result.push({
                        ...item,
                        children,
                        isLoaded: true,
                        isExpanded: true,
                    });
                } else {
                    result.push(item);
                }
            }
            return result;
        },
        [fetchChildren]
    );

    useEffect(() => {
        setNodes([]);
        setSearchQuery('');
        setIsLoading(true);
        const load = async () => {
            try {
                if (initialExpandedFolderIds.size > 0) {
                    const tree = await loadWithExpandedFolders(initialExpandedFolderIds);
                    setNodes(tree);
                } else {
                    const rootNodes = await fetchChildren();
                    setNodes(rootNodes);
                }
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [integrationId, driveId]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateNode = useCallback(
        (nodeList: TreeNode[], nodeId: string, updater: (node: TreeNode) => TreeNode): TreeNode[] => {
            return nodeList.map((node) => {
                if (node.id === nodeId) return updater(node);
                if (node.children) {
                    return { ...node, children: updateNode(node.children, nodeId, updater) };
                }
                return node;
            });
        },
        []
    );

    const handleToggleExpand = useCallback(
        async (nodeId: string) => {
            const findNode = (list: TreeNode[]): TreeNode | undefined => {
                for (const n of list) {
                    if (n.id === nodeId) return n;
                    if (n.children) {
                        const found = findNode(n.children);
                        if (found) return found;
                    }
                }
                return undefined;
            };

            // Read current nodes via ref to avoid stale closure
            const node = findNode(nodesRef.current);
            if (!node) return;

            if (!node.isLoaded) {
                const children = await fetchChildren(nodeId);
                setNodes((prev) =>
                    updateNode(prev, nodeId, (n) => ({
                        ...n,
                        children,
                        isLoaded: true,
                        isExpanded: true,
                    }))
                );
            } else {
                setNodes((prev) =>
                    updateNode(prev, nodeId, (n) => ({
                        ...n,
                        isExpanded: !n.isExpanded,
                    }))
                );
            }
        },
        [fetchChildren, updateNode]
    );

    const getAllDescendantIds = useCallback((node: TreeNode): string[] => {
        if (!node.isFolder && !isFileSupported(node.name)) return [];
        const ids = [node.id];
        if (node.children) {
            for (const child of node.children) {
                ids.push(...getAllDescendantIds(child));
            }
        }
        return ids;
    }, []);

    const emitSelectionChange = useCallback(
        (newSelectedIds: string[]) => {
            const newSelectedSet = new Set(newSelectedIds);
            const items = buildSelectedItemsWithPaths(nodesRef.current, newSelectedSet);
            onSelectedItemsChange(items);
        },
        [onSelectedItemsChange]
    );

    const handleToggleSelect = useCallback(
        (node: TreeNode) => {
            const idsToRemove = new Set(getAllDescendantIds(node));
            const remaining = selectedIds.filter((id) => !idsToRemove.has(id));
            // Select: keep the node itself (descendants are now covered). Deselect: drop everything.
            emitSelectionChange(selectedSet.has(node.id) ? remaining : [...remaining, node.id]);
        },
        [selectedIds, selectedSet, emitSelectionChange, getAllDescendantIds]
    );

    const rootSelectableIds = useMemo(
        () => nodes.filter((n) => n.isFolder || isFileSupported(n.name)).map((n) => n.id),
        [nodes]
    );
    const allSelected = rootSelectableIds.length > 0 && rootSelectableIds.every((id) => selectedSet.has(id));
    const someSelected = !allSelected && selectedIds.length > 0;

    const handleSelectAll = useCallback(() => {
        emitSelectionChange(allSelected ? [] : rootSelectableIds);
    }, [allSelected, rootSelectableIds, emitSelectionChange]);

    const filterNodes = useCallback(
        (nodeList: TreeNode[]): TreeNode[] => {
            if (!searchQuery) return nodeList;
            return nodeList
                .map((node) => {
                    const nameMatches = node.name.toLowerCase().includes(searchQuery.toLowerCase());
                    const filteredChildren = node.children ? filterNodes(node.children) : undefined;
                    const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;

                    if (nameMatches || hasMatchingChildren) {
                        return {
                            ...node,
                            children: filteredChildren,
                            isExpanded: hasMatchingChildren ? true : node.isExpanded,
                        };
                    }
                    return null;
                })
                .filter(Boolean) as TreeNode[];
        },
        [searchQuery]
    );

    const visibleNodes = useMemo(() => filterNodes(nodes), [filterNodes, nodes]);

    return (
        <div className="flex flex-col gap-2">
            <StringField
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder={t('toolProviders.sharepoint.searchPlaceholder')}
            />

            <label className="flex items-center gap-2 cursor-pointer py-1.5 border-b border-line-weak">
                <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll} />
                <span className="text-sm text-content-strong">{t('toolProviders.sharepoint.selectAll')}</span>
            </label>

            <div className="max-h-72 overflow-y-auto">
                {isLoading && nodes.length === 0 && (
                    <div className="flex items-center justify-center py-6 text-sm text-content-weak">
                        {t('common.loading')}
                    </div>
                )}
                {visibleNodes.map((node) => (
                    <TreeNodeRow
                        key={node.id}
                        node={node}
                        depth={0}
                        selectedSet={selectedSet}
                        onToggleExpand={handleToggleExpand}
                        onToggleSelect={handleToggleSelect}
                        parentSelected={false}
                    />
                ))}
            </div>
        </div>
    );
};

const getNodeSelectionState = (node: TreeNode, selectedSet: Set<string>): 'checked' | 'indeterminate' | 'unchecked' => {
    if (selectedSet.has(node.id)) return 'checked';
    if (!node.isFolder || !node.children || node.children.length === 0) {
        return 'unchecked';
    }
    // Folder is only 'checked' via the early return above (folder ID in selectedSet).
    // Any mix of selected descendants without the folder itself selected → 'indeterminate'.
    const hasSelectedDescendant = node.children.some(
        (child) => getNodeSelectionState(child, selectedSet) !== 'unchecked'
    );
    return hasSelectedDescendant ? 'indeterminate' : 'unchecked';
};

const TreeNodeRow = ({
    node,
    depth,
    selectedSet,
    onToggleExpand,
    onToggleSelect,
    parentSelected,
}: {
    node: TreeNode;
    depth: number;
    selectedSet: Set<string>;
    onToggleExpand: (id: string) => void;
    onToggleSelect: (node: TreeNode) => void;
    parentSelected: boolean;
}) => {
    const selectionState = parentSelected ? 'checked' : getNodeSelectionState(node, selectedSet);
    const ItemIcon = node.isFolder ? FolderIcon : getFileIcon(node.name);
    const unsupported = !node.isFolder && !isFileSupported(node.name);

    return (
        <>
            <div
                className={classnames(
                    'flex items-center gap-1.5 py-1.5 hover:bg-surface-medium rounded-md transition-colors',
                    unsupported && 'opacity-50'
                )}
                style={{ paddingLeft: `${depth * 1.5 + 0.25}rem` }}
            >
                {node.isFolder ? (
                    <button
                        type="button"
                        onClick={() => onToggleExpand(node.id)}
                        className="flex items-center justify-center w-5 h-5 text-content-weak hover:text-content-strong flex-none cursor-pointer bg-transparent border-none p-0"
                    >
                        {node.isExpanded ? (
                            <ChevronDownIcon className="text-xs" />
                        ) : (
                            <ChevronRightIcon className="text-xs" />
                        )}
                    </button>
                ) : (
                    <span className="w-5 flex-none" />
                )}

                <label
                    className={classnames(
                        'flex items-center gap-2 flex-1 min-w-0',
                        parentSelected || unsupported ? 'cursor-not-allowed' : 'cursor-pointer'
                    )}
                >
                    <Checkbox
                        checked={selectionState === 'checked'}
                        indeterminate={selectionState === 'indeterminate'}
                        onChange={() => onToggleSelect(node)}
                        disabled={parentSelected || unsupported}
                    />
                    <ItemIcon
                        className={classnames(
                            'text-sm flex-none',
                            node.isFolder ? 'text-content-medium' : 'text-content-weak'
                        )}
                    />
                    <span className="text-sm text-content-strong truncate">{node.name}</span>
                </label>
            </div>

            {node.isFolder &&
                node.isExpanded &&
                node.children?.map((child) => (
                    <TreeNodeRow
                        key={child.id}
                        node={child}
                        depth={depth + 1}
                        selectedSet={selectedSet}
                        onToggleExpand={onToggleExpand}
                        onToggleSelect={onToggleSelect}
                        parentSelected={parentSelected || selectedSet.has(node.id)}
                    />
                ))}
        </>
    );
};

export default SharePointTreeBrowser;
